import { translateService } from "../services/TranslateService";
import fiFI from "./fi-fi.json";

// Only Finnish ships in the entry chunk (improvement row I2). English — one visitor in eight
// searches in it — is a lazy chunk: on a lang="en" shortcode page the plugin announces it from
// the head next to the entry (twentyfourrent_head_links), so the two download together and the
// first paint is already English; a ?lang=en page pays one round trip. The entry's `render`
// and App's language effect both wait for it through ensureLanguage().
translateService.registerTranslations("fi", fiFI);

export type WidgetLanguage = 'fi' | 'en';

const loading: Partial<Record<WidgetLanguage, Promise<void>>> = {};

/**
 * The language the page starts in, read the way App.tsx reads it: an explicit ?lang= first
 * (only "en" means English, anything else is Finnish), else the shortcode's lang attribute.
 */
export function initialLanguage(search: string, shortcodeLang: unknown): WidgetLanguage {
    const urlLang = new URLSearchParams(search).get('lang');
    if (urlLang) return urlLang === 'en' ? 'en' : 'fi';
    return shortcodeLang === 'en' ? 'en' : 'fi';
}

/**
 * Makes sure a language's translations are registered. Returns undefined when nothing has to
 * load (Finnish, or English already registered), otherwise the load in progress — the same
 * promise for every caller. A failed load is forgotten so the next call can try again.
 */
export function ensureLanguage(lang: string): Promise<void> | undefined {
    if (lang !== 'en' || translateService.hasTranslations('en')) return undefined;
    loading.en ??= import('./en-gb.json')
        .then(module => { translateService.registerTranslations('en', module.default); })
        .catch(error => { delete loading.en; throw error; });
    return loading.en;
}

/** How long the first render waits for a language before rendering in Finnish anyway. */
export const LANGUAGE_WAIT_MS = 2500;

/**
 * Calls `start` exactly once: as soon as the language is registered, its load fails, or
 * `timeoutMs` passes — whichever comes first. A hanging chunk request (no preload, a slow
 * phone network) must not keep the widget blank; App's language effect shares the same
 * load and switches to English when it does arrive.
 */
export function startWhenLanguageReady(lang: string, start: () => void, timeoutMs = LANGUAGE_WAIT_MS): void {
    const pending = ensureLanguage(lang);
    if (!pending) { start(); return; }
    let started = false;
    const once = () => {
        if (started) return;
        started = true;
        clearTimeout(timer);
        start();
    };
    const timer = setTimeout(once, timeoutMs);
    pending.then(once, once);
}
